import { type ProspectDto, type ProspectStage } from "@crowdcoursing/api/openapi";
import { ContactPage, Description, Edit, Save, TaskAlt, Timeline } from "@mui/icons-material";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select as MuiSelect,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { SalesOwnerAvatar } from "~ui/molecules/SalesOwnerAvatar/SalesOwnerAvatar";
import { PROSPECT_TYPES } from "~utils/constants/prospectTypes";
import { STAGES } from "~utils/constants/stages";

import { EditableDate } from "../EditableDate/EditableDate";
import { EditableEstimatedValue } from "../EditableEstimatedValue/EditableEstimatedValue";

const tAddContact = "Add Contact";
const tAddNote = "Add Note";
const tAddInteraction = "Add Interaction";
const tAddTask = "Add Task";
const tOwner = "Owner";
const tStage = "Stage";
const tType = "Type";
const tEstimatedValue = "Estimated Value";
const tExpectedCloseDate = "Expected Close Date";
const tOpenTasks = "Open Tasks";

type ProspectUpdateField = "name" | "type" | "estimatedValue" | "prospectType" | "expectedCloseDate";

interface ProspectHeaderProps {
  prospect: ProspectDto;
  onFieldUpdate: (field: ProspectUpdateField, value: string | number) => Promise<void>;
  onStageChange: (stage: ProspectStage) => Promise<void>;
  onAddContact?: () => void;
  onAddNote?: () => void;
  onAddInteraction?: () => void;
  onAddTask?: () => void;
}

export const ProspectHeader = ({
  prospect,
  onFieldUpdate,
  onStageChange,
  onAddContact,
  onAddNote,
  onAddInteraction,
  onAddTask,
}: ProspectHeaderProps) => {
  const [isEditingName, setIsEditingName] = useState(false);

  const [isUpdatingStage, setIsUpdatingStage] = useState(false);
  const [isUpdatingType, setIsUpdatingType] = useState(false);
  const [tempName, setTempName] = useState(prospect.name);

  const handleNameSave = async () => {
    try {
      await onFieldUpdate("name", tempName ?? "");
      setIsEditingName(false);
    } catch {
      // Error handling is done in the hook
      setTempName(prospect.name);
    }
  };

  const handleStageChange = async (event: { target: { value: string } }) => {
    setIsUpdatingStage(true);
    try {
      await onStageChange(Number(event.target.value) as ProspectStage);
    } finally {
      setIsUpdatingStage(false);
    }
  };

  const handleTypeChange = async (event: { target: { value: string } }) => {
    setIsUpdatingType(true);
    try {
      await onFieldUpdate("prospectType", Number(event.target.value));
    } finally {
      setIsUpdatingType(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" flexDirection="column" gap={3}>
        {/* Name and Quick Actions */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {isEditingName ? (
              <>
                <TextField
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  fullWidth
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleNameSave();
                    }
                  }}
                />
                <IconButton onClick={handleNameSave} size="small" aria-label="Save prospect name">
                  <Save />
                </IconButton>
              </>
            ) : (
              <>
                <Typography variant="h5">{prospect.name}</Typography>
                <IconButton onClick={() => setIsEditingName(true)} size="small" aria-label="Edit prospect name">
                  <Edit />
                </IconButton>
              </>
            )}
          </Box>

          {/* Quick Actions */}
          <Stack direction="row" spacing={1}>
            <Tooltip title={tAddInteraction}>
              <IconButton
                size="small"
                onClick={onAddInteraction}
                disabled={!onAddInteraction}
                color="primary"
                aria-label={tAddInteraction}
              >
                <Timeline fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={tAddContact}>
              <IconButton
                size="small"
                onClick={onAddContact}
                disabled={!onAddContact}
                color="primary"
                aria-label={tAddContact}
              >
                <ContactPage fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={tAddNote}>
              <IconButton size="small" onClick={onAddNote} disabled={!onAddNote} color="primary" aria-label={tAddNote}>
                <Description fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={tAddTask}>
              <IconButton size="small" onClick={onAddTask} disabled={!onAddTask} color="primary" aria-label={tAddTask}>
                <TaskAlt fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Owner */}
        {(prospect.ownerFirstName || prospect.ownerLastName) && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {tOwner}
            </Typography>
            <SalesOwnerAvatar
              userId={prospect.ownerUserId}
              firstName={prospect.ownerFirstName}
              lastName={prospect.ownerLastName}
              size="medium"
            />
          </Box>
        )}

        {/* Stage & Type Selection */}
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <FormControl>
            <InputLabel id="stage-select-label">{tStage}</InputLabel>
            <MuiSelect
              labelId="stage-select-label"
              id="stage-select"
              value={(prospect.stage ?? 0).toString()}
              onChange={handleStageChange}
              disabled={isUpdatingStage}
              label={tStage}
              aria-label={`Select prospect stage, current stage: ${STAGES[prospect.stage ?? 0]?.name}`}
            >
              {Object.entries(STAGES).map(([value, { emoji, name }]) => (
                <MenuItem key={value} value={value} aria-label={name}>
                  <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                    <span aria-hidden="true">{emoji}</span>
                    <span>{name}</span>
                  </Box>
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl>
            <InputLabel id="type-select-label">{tType}</InputLabel>
            <MuiSelect
              labelId="type-select-label"
              id="type-select"
              value={(prospect.prospectType ?? 1).toString()}
              onChange={handleTypeChange}
              disabled={isUpdatingType}
              label={tType}
              aria-label={`Select prospect type, current type: ${PROSPECT_TYPES.find((t) => t.id === prospect.prospectType)?.name}`}
            >
              {PROSPECT_TYPES.map(({ id, name }) => (
                <MenuItem key={id} value={id} aria-label={name}>
                  {name}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </Box>

        {/* Key Metrics */}
        <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {tEstimatedValue}
            </Typography>
            <EditableEstimatedValue
              value={prospect.estimatedValue ?? 0}
              onUpdate={(value) => onFieldUpdate("estimatedValue", value ?? 0)}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {tExpectedCloseDate}
            </Typography>
            <EditableDate
              value={prospect.expectedCloseDate ?? undefined}
              onUpdate={(value) => onFieldUpdate("expectedCloseDate", value ?? "")}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {tOpenTasks}
            </Typography>
            <Typography variant="h6">{prospect.tasks?.filter((t) => t.status !== 4).length ?? 0}</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
